import React, { useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SvgIcon, SvgIconProps } from '@mui/material';
import { delayed, exitTween, RENDER_EVERY_FRAME, SPRING } from './motionTokens';

// A value that changes in place (counts, steppers, "1 / 3") rolls in the direction of the
// change with the shared blur, instead of swapping instantly. Pass `direction` 1 for an
// increase, -1 for a decrease (defaults to rolling up).
export const RollingValue: React.FC<{ value: React.ReactNode; valueKey: string | number; direction?: 1 | -1 }> = ({
  value,
  valueKey,
  direction = 1,
}) => (
  <span style={{ position: 'relative', display: 'inline-grid', overflow: 'hidden', verticalAlign: 'bottom' }}>
    <AnimatePresence initial={false} custom={direction} mode="popLayout">
      <motion.span
        {...RENDER_EVERY_FRAME}
        key={valueKey}
        initial={{ y: 12 * direction, opacity: 0, filter: 'blur(4px)' }}
        animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
        exit={{ y: -12 * direction, opacity: 0, filter: 'blur(4px)', transition: exitTween(0.12) }}
        transition={SPRING.snap}
        style={{ gridArea: '1 / 1', display: 'inline-block', whiteSpace: 'nowrap' }}
      >
        {value}
      </motion.span>
    </AnimatePresence>
  </span>
);

// Keeps the direction of the last change of a number, for RollingValue.
export const useChangeDirection = (value: number): 1 | -1 => {
  const previous = useRef(value);
  const direction = useRef<1 | -1>(1);
  if (value !== previous.current) {
    direction.current = value > previous.current ? 1 : -1;
    previous.current = value;
  }
  return direction.current;
};

// MUI Checkbox checked icon (set app-wide in the theme): same filled rounded square as MUI's
// CheckBox icon, but the square settles in and the tick draws itself when it becomes checked.
export const AnimatedCheckboxIcon: React.FC<SvgIconProps> = props => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <motion.rect
      x={3}
      y={3}
      width={18}
      height={18}
      rx={2}
      fill="currentColor"
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ ...SPRING.snap, opacity: { duration: 0.1 } }}
      style={{ transformOrigin: '12px 12px' }}
    />
    <motion.path
      d="M7 12.4 L10.3 15.6 L17.2 8.6"
      fill="none"
      stroke="#fff"
      strokeWidth={2.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={delayed(SPRING.snap, 0.08)}
    />
  </SvgIcon>
);
